import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Avatar,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';

// 帶有動畫效果的LINE風格對話框組件 - 修改為LINE風格的白色客服、綠色用戶消息
const AnimatedLineChat = ({ messages }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  // Get translated text based on current language
  const getLineHeaderText = () => {
    switch(currentLang) {
      case 'ja':
        return 'LINE対話';
      case 'en':
        return 'LINE Chat';
      case 'zh-CN':
        return 'LINE对话';
      default: // zh-TW
        return 'LINE對話';
    }
  };

  const getInputPlaceholder = () => {
    switch(currentLang) {
      case 'ja':
        return 'メッセージを入力...';
      case 'en':
        return 'Type a message...';
      case 'zh-CN':
        return '输入消息...';
      default: // zh-TW
        return '輸入訊息...';
    }
  };

  const getReplayButtonText = () => {
    switch(currentLang) {
      case 'ja':
        return '再生';
      case 'en':
        return 'Replay';
      case 'zh-CN':
        return '重新播放';
      default: // zh-TW
        return '重新播放';
    }
  };
  
  // 檢測裝置是否為手機
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // 初始檢查
    checkIfMobile();
    
    // 窗口大小變化時重新檢查
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Reset animation when language or messages change
  useEffect(() => {
    setDisplayedMessages([]);
    setCurrentIndex(0);
    setIsPlaying(true);
  }, [messages, currentLang]); // Add currentLang as dependency
  
  // 自動添加新消息 - 第一條消息在300ms後出現，後續每2秒一條
  useEffect(() => {
    if (isPlaying && currentIndex < messages.length) {
      const delay = currentIndex === 0 ? 300 : 2000; // 第一條消息300ms，其他2000ms
      
      const timer = setTimeout(() => {
        setDisplayedMessages(prev => [...prev, messages[currentIndex]]);
        setCurrentIndex(prev => prev + 1);
        
        // 如果是最後一條消息，停止播放
        if (currentIndex === messages.length - 1) {
          setIsPlaying(false);
        }
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [messages, currentIndex, isPlaying]);
  
  // 重新播放功能
  const handleRestart = () => {
    setDisplayedMessages([]);
    setCurrentIndex(0);
    setIsPlaying(true);
    
    // 重置滾動位置但不強制滾動
    if (chatContainerRef.current) {
      // 這裡我們不再設置 scrollTop，讓用戶保持當前的滾動位置
    }
  };
  
  return (
    <Box sx={{ 
      maxWidth: 400, 
      mx: 'auto', 
      my: 4, 
      bgcolor: '#f2e8d8', // LINE典型的米色背景
      borderRadius: 2,
      overflow: 'hidden',
      boxShadow: 3
    }}>
      {/* LINE標頭 */}
      <Box sx={{ 
        p: 1.5, 
        bgcolor: '#e6c59c', // LINE淺棕色標頭
        color: '#6f5e53',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0d0bc'
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', fontSize: '16px' }}>
          {getLineHeaderText()}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            position: 'absolute', 
            right: 12,
            color: '#6f5e53',
            fontSize: '14px'
          }}
        >
          9:25
        </Typography>
      </Box>
      
      {/* 對話內容 */}
      <Box 
        ref={chatContainerRef}
        sx={{ 
          p: 2,
          height: 640,
          overflowY: 'auto',
          overscrollBehavior: 'contain', // 防止過度滾動
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          backgroundImage: 'linear-gradient(to bottom, #f2e8d8, #ede3d1)',
        }}
      >
        {displayedMessages.map((msg, index) => (
          <Box 
            key={`${index}-${currentLang}`}
            sx={{
              display: 'flex',
              justifyContent: msg.isUser || msg.sender === 'customer' ? 'flex-end' : 'flex-start',
              mb: 3,
              animation: 'fadeIn 0.5s ease',
              '@keyframes fadeIn': {
                from: { 
                  opacity: 0,
                  transform: 'translateY(10px)'
                },
                to: { 
                  opacity: 1,
                  transform: 'translateY(0)'
                }
              }
            }}
          >
            {!msg.isUser && msg.sender !== 'customer' && (
              <Avatar
                sx={{ 
                  width: 36, 
                  height: 36,
                  mr: 1,
                  borderRadius: '50%', // LINE頭像的圓角方形風格
                  bgcolor: '#ccc',
                  border: '1px solid #7f7f7f',
                  color: "#000"
                }}
              >
                {msg.avatar || 'B'}
              </Avatar>
            )}
            <Box 
              sx={{ 
                maxWidth: '70%',
                p: 1.5,
                borderRadius: (msg.isUser || msg.sender === 'customer') ? '20px 0 20px 20px' : '0 20px 20px 20px', // LINE消息泡泡造型
                bgcolor: (msg.isUser || msg.sender === 'customer') ? '#ede3d1' : '#ffffff', // 用戶是米色，客服是白色
                color: '#000',
                position: 'relative',
                boxShadow: '1 2px 2px rgba(0,0,0,0.1)',
                border: '1px solid #7f7f7f', // 客服消息帶有細邊框
                wordBreak: 'break-word'
              }}
            >
              <Typography variant="body2" sx={{ lineHeight: 1.4, fontSize: '16px' }}>
                {msg.text}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block',
                  textAlign: (msg.isUser || msg.sender === 'customer') ? 'right' : 'left',
                  color: '#9b9b9b',
                  fontSize: '12px',
                  mt: 0.5,
                  mb: -0.5
                }}
              >
                {msg.time || `9:${25 + index} AM`}
              </Typography>
            </Box>
          </Box>
        ))}
        <div ref={messagesEndRef} />
        
        {/* 播放完畢顯示重新播放按鈕 */}
        {!isPlaying && currentIndex === messages.length && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 2 
          }}>
            <Button 
              variant="contained" 
              onClick={handleRestart}
              sx={{
                bgcolor: '#91cc52',
                color: '#000',
                '&:hover': {
                  bgcolor: '#7fb843',
                },
                fontWeight: 'medium',
                borderRadius: '20px',
                px: 3,
                fontSize: '16px'
              }}
            >
              {getReplayButtonText()}
            </Button>
          </Box>
        )}
      </Box>
      
      {/* LINE底部輸入區域 */}
      <Box sx={{ 
        p: 1.5, 
        bgcolor: '#e6c59c',
        borderTop: '1px solid #e0d0bc',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Box sx={{ 
          flexGrow: 1, 
          bgcolor: '#fff', 
          borderRadius: '20px',
          height: '36px',
          pl: 2,
          display: 'flex',
          alignItems: 'center',
          color: '#aaa'
        }}>
          <Typography variant="body2" sx={{ fontSize: '16px' }}>
            {getInputPlaceholder()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AnimatedLineChat;